import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { TermsAndConditionsModal } from './TermsAndConditionsModal';
interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
}
type DocType = 'CEDULA' | 'PASAPORTE';
interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}
const countries: Country[] = [{
  code: 'EC',
  name: 'Ecuador',
  dialCode: '+593',
  flag: 'https://flagcdn.com/w20/ec.png'
}, {
  code: 'AR',
  name: 'Argentina',
  dialCode: '+54',
  flag: 'https://flagcdn.com/w20/ar.png'
}, {
  code: 'BO',
  name: 'Bolivia',
  dialCode: '+591',
  flag: 'https://flagcdn.com/w20/bo.png'
}, {
  code: 'BR',
  name: 'Brasil',
  dialCode: '+55',
  flag: 'https://flagcdn.com/w20/br.png'
}, {
  code: 'CL',
  name: 'Chile',
  dialCode: '+56',
  flag: 'https://flagcdn.com/w20/cl.png'
}, {
  code: 'CO',
  name: 'Colombia',
  dialCode: '+57',
  flag: 'https://flagcdn.com/w20/co.png'
}, {
  code: 'CR',
  name: 'Costa Rica',
  dialCode: '+506',
  flag: 'https://flagcdn.com/w20/cr.png'
}, {
  code: 'CU',
  name: 'Cuba',
  dialCode: '+53',
  flag: 'https://flagcdn.com/w20/cu.png'
}, {
  code: 'DO',
  name: 'Rep. Dominicana',
  dialCode: '+1',
  flag: 'https://flagcdn.com/w20/do.png'
}, {
  code: 'SV',
  name: 'El Salvador',
  dialCode: '+503',
  flag: 'https://flagcdn.com/w20/sv.png'
}, {
  code: 'GT',
  name: 'Guatemala',
  dialCode: '+502',
  flag: 'https://flagcdn.com/w20/gt.png'
}, {
  code: 'HN',
  name: 'Honduras',
  dialCode: '+504',
  flag: 'https://flagcdn.com/w20/hn.png'
}, {
  code: 'MX',
  name: 'México',
  dialCode: '+52',
  flag: 'https://flagcdn.com/w20/mx.png'
}, {
  code: 'NI',
  name: 'Nicaragua',
  dialCode: '+505',
  flag: 'https://flagcdn.com/w20/ni.png'
}, {
  code: 'PA',
  name: 'Panamá',
  dialCode: '+507',
  flag: 'https://flagcdn.com/w20/pa.png'
}, {
  code: 'PY',
  name: 'Paraguay',
  dialCode: '+595',
  flag: 'https://flagcdn.com/w20/py.png'
}, {
  code: 'PE',
  name: 'Perú',
  dialCode: '+51',
  flag: 'https://flagcdn.com/w20/pe.png'
}, {
  code: 'PR',
  name: 'Puerto Rico',
  dialCode: '+1',
  flag: 'https://flagcdn.com/w20/pr.png'
}, {
  code: 'UY',
  name: 'Uruguay',
  dialCode: '+598',
  flag: 'https://flagcdn.com/w20/uy.png'
}, {
  code: 'VE',
  name: 'Venezuela',
  dialCode: '+58',
  flag: 'https://flagcdn.com/w20/ve.png'
}, {
  code: 'ES',
  name: 'España',
  dialCode: '+34',
  flag: 'https://flagcdn.com/w20/es.png'
}, {
  code: 'US',
  name: 'Estados Unidos',
  dialCode: '+1',
  flag: 'https://flagcdn.com/w20/us.png'
}];
// Helper function to parse and format error messages
const getErrorMessage = (data: any, response: Response): string => {
  // Check for API response with CodigoRespuesta and DescripcionRespuesta
  if (data?.CodigoRespuesta || data?.DescripcionRespuesta) {
    const codigo = data.CodigoRespuesta;
    const descripcion = data.DescripcionRespuesta || '';
    // CodigoRespuesta "000" typically means success, anything else is an error
    if (codigo !== '000') {
      const lowerDesc = descripcion.toLowerCase();
      // Check for duplicate/already registered
      if (lowerDesc.includes('ya fue registrado') || lowerDesc.includes('ya registrado') || lowerDesc.includes('ya existe')) {
        return 'Este usuario ya está registrado en el sistema. Si necesitas ayuda, contacta con soporte.';
      }
      // Return the API description if available
      if (descripcion) {
        return descripcion;
      }
      // Fallback based on error code
      return `Error ${codigo}: No se pudo completar el registro.`;
    }
  }
  // Check for other common error message fields
  const apiMessage = data?.message || data?.error || data?.mensaje || data?.descripcion;
  if (apiMessage) {
    const lowerMessage = apiMessage.toLowerCase();
    // Check for duplicate/already registered errors
    if (lowerMessage.includes('ya existe') || lowerMessage.includes('ya registrado') || lowerMessage.includes('duplicado') || lowerMessage.includes('already exists') || lowerMessage.includes('duplicate')) {
      return 'Este usuario ya está registrado. Si olvidaste tu contraseña, contacta con soporte.';
    }
    // Check for invalid data errors
    if (lowerMessage.includes('inválido') || lowerMessage.includes('invalid') || lowerMessage.includes('formato incorrecto')) {
      return `Datos inválidos: ${apiMessage}`;
    }
    // Check for missing required fields
    if (lowerMessage.includes('requerido') || lowerMessage.includes('required') || lowerMessage.includes('obligatorio')) {
      return `Campo requerido: ${apiMessage}`;
    }
    // Return the original API message if it's user-friendly
    if (apiMessage.length < 150) {
      return apiMessage;
    }
  }
  // HTTP status-based messages
  if (response.status === 400) {
    return 'Los datos enviados son incorrectos. Por favor verifica la información.';
  }
  if (response.status === 409) {
    return 'Este usuario ya está registrado en el sistema.';
  }
  if (response.status === 500) {
    return 'Error en el servidor. Por favor intenta nuevamente en unos minutos.';
  }
  if (response.status === 503) {
    return 'El servicio no está disponible temporalmente. Intenta más tarde.';
  }
  // Default error message
  return 'Hubo un error al procesar tu registro. Por favor intenta nuevamente o contacta con soporte.';
};
export function SignupModal({
  isOpen,
  onClose
}: SignupModalProps) {
  const [docType, setDocType] = useState<DocType>('CEDULA');
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]); // Ecuador by default
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '',
    lastName: '',
    firstName: '',
    phone: '',
    email: '',
    acceptedTerms: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const validate = () => {
    const newErrors: Record<string, string> = {};
    // Document number validation based on type
    if (!formData.docNumber) {
      newErrors.docNumber = 'Este campo es requerido';
    } else if (docType === 'CEDULA') {
      // Cedula: exactly 10 numeric digits
      const cedulaRegex = /^\d{10}$/;
      if (!cedulaRegex.test(formData.docNumber)) {
        newErrors.docNumber = 'La cédula debe tener exactamente 10 dígitos numéricos';
      }
    } else if (docType === 'PASAPORTE') {
      // Pasaporte: 3-13 alphanumeric characters
      const pasaporteRegex = /^[A-Za-z0-9]{3,13}$/;
      if (!pasaporteRegex.test(formData.docNumber)) {
        newErrors.docNumber = 'El pasaporte debe tener entre 3 y 13 caracteres alfanuméricos';
      }
    }
    if (!formData.lastName) newErrors.lastName = 'El apellido es requerido';
    if (!formData.firstName) newErrors.firstName = 'El nombre es requerido';
    if (!formData.phone) newErrors.phone = 'El teléfono es requerido';
    if (!formData.email) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.acceptedTerms) newErrors.acceptedTerms = 'Debes aceptar los términos';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setShowError(false);
    try {
      const response = await fetch('https://admin.benefika.net/webservice/comprobante/api/RegistroUsuarioConvenio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authentication: 'f23f90bc750e241604ce3afbda15226b090808a0e77a5237f7e8713883a5c460'
        },
        body: JSON.stringify({
          TipoIdentificacion: docType,
          Cedula: formData.docNumber,
          Nombre: formData.firstName,
          Apellido: formData.lastName,
          Email: formData.email,
          Telefono: formData.phone
        })
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, throw a generic error
        throw new Error('Error al procesar la respuesta del servidor.');
      }
      // Log the response for debugging
      console.log('API Response:', {
        status: response.status,
        data
      });
      // Check for errors based on CodigoRespuesta
      // "000" = success, anything else = error
      const isSuccess = data.CodigoRespuesta === '000';
      if (!isSuccess) {
        const errorMsg = getErrorMessage(data, response);
        throw new Error(errorMsg);
      }
      // Additional fallback checks for other error formats
      if (!response.ok || data.error || data.status === 'error' || data.success === false) {
        const errorMsg = getErrorMessage(data, response);
        throw new Error(errorMsg);
      }
      // Show success message
      setShowSuccess(true);
      // Reset form
      setFormData({
        docNumber: '',
        lastName: '',
        firstName: '',
        phone: '',
        email: '',
        acceptedTerms: false
      });
      setDocType('CEDULA');
      setSelectedCountry(countries[0]);
      // Auto close after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error al registrar:', error);
      const message = error instanceof Error ? error.message : 'Hubo un error al procesar tu registro. Por favor intenta nuevamente.';
      setErrorMessage(message);
      setShowError(true);
      // Auto hide error after 8 seconds (longer for users to read)
      setTimeout(() => {
        setShowError(false);
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handleClose = () => {
    setShowSuccess(false);
    setShowError(false);
    onClose();
  };
  return <>
      <AnimatePresence data-id="element-199">
        {isOpen && !showPrivacyModal && !showTermsModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={handleClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-200">
              {/* Modal Container */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} onClick={e => e.stopPropagation()} className="relative w-full max-w-lg bg-gaming-card border border-gaming-cyan/30 rounded-xl shadow-[0_0_50px_rgba(0,217,255,0.15)] overflow-hidden" data-id="element-201">
                {/* Success Message */}
                <AnimatePresence data-id="element-202">
                  {showSuccess && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="absolute inset-0 bg-gaming-dark/95 backdrop-blur-lg z-50 flex flex-col items-center justify-center p-8" data-id="element-203">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.2,
                  type: 'spring',
                  stiffness: 200
                }} data-id="element-204">
                        <CheckCircle className="w-24 h-24 text-gaming-cyan mb-6" data-id="element-205" />
                      </motion.div>
                      <motion.h3 initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.3
                }} className="text-3xl font-bold text-white mb-3 text-center" data-id="element-206">
                        ¡Registro Exitoso!
                      </motion.h3>
                      <motion.p initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4
                }} className="text-xl text-gaming-cyan text-center" data-id="element-207">
                        Bienvenido a LevelUp Play
                      </motion.p>
                      <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.5
                }} className="mt-8" data-id="element-208">
                        <div className="flex gap-2 text-gray-400 text-sm" data-id="element-209">
                          <div className="w-2 h-2 bg-gaming-cyan rounded-full animate-pulse" data-id="element-210"></div>
                          <div className="w-2 h-2 bg-gaming-cyan rounded-full animate-pulse" style={{
                      animationDelay: '0.2s'
                    }} data-id="element-211"></div>
                          <div className="w-2 h-2 bg-gaming-cyan rounded-full animate-pulse" style={{
                      animationDelay: '0.4s'
                    }} data-id="element-212"></div>
                        </div>
                      </motion.div>
                    </motion.div>}
                </AnimatePresence>
                {/* Error Message */}
                <AnimatePresence data-id="element-213">
                  {showError && <motion.div initial={{
                opacity: 0,
                y: -20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -20
              }} className="absolute top-0 left-0 right-0 z-40 p-4" data-id="element-214">
                      <div className="bg-red-500/90 backdrop-blur-lg border border-red-400 rounded-lg p-4 flex items-start gap-3 shadow-lg" data-id="element-215">
                        <AlertCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" data-id="element-216" />
                        <div className="flex-1" data-id="element-217">
                          <h4 className="text-white font-bold mb-1" data-id="element-218">
                            Error en el registro
                          </h4>
                          <p className="text-white/90 text-sm leading-relaxed" data-id="element-219">
                            {errorMessage}
                          </p>
                        </div>
                        <button onClick={() => setShowError(false)} className="text-white/80 hover:text-white transition-colors" data-id="element-220">
                          <X className="w-4 h-4" data-id="element-221" />
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
                {/* Header */}
                <div className="p-6 border-b border-white/10 flex justify-between items-center bg-gaming-dark/50" data-id="element-222">
                  <h2 className="text-2xl font-bold font-display text-white uppercase tracking-wide" data-id="element-223">
                    Crear <span className="text-gaming-cyan" data-id="element-224">Cuenta</span>
                  </h2>
                  <button onClick={handleClose} className="text-gray-400 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors" data-id="element-225">
                    <X className="w-5 h-5" data-id="element-226" />
                  </button>
                </div>
                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-227">
                  {/* Document Type Radio */}
                  <div className="flex gap-4 mb-2" data-id="element-228">
                    <label className={`flex-1 cursor-pointer border rounded-lg p-3 flex items-center justify-center gap-2 transition-all ${docType === 'CEDULA' ? 'border-gaming-cyan bg-gaming-cyan/10 text-white' : 'border-white/10 text-gray-400 hover:border-white/30'}`} data-id="element-229">
                      <input type="radio" name="docType" className="hidden" checked={docType === 'CEDULA'} onChange={() => setDocType('CEDULA')} data-id="element-230" />
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${docType === 'CEDULA' ? 'border-gaming-cyan' : 'border-gray-500'}`} data-id="element-231">
                        {docType === 'CEDULA' && <div className="w-2 h-2 bg-gaming-cyan rounded-full" data-id="element-232" />}
                      </div>
                      <span className="font-medium" data-id="element-233">Cédula</span>
                    </label>
                    <label className={`flex-1 cursor-pointer border rounded-lg p-3 flex items-center justify-center gap-2 transition-all ${docType === 'PASAPORTE' ? 'border-gaming-cyan bg-gaming-cyan/10 text-white' : 'border-white/10 text-gray-400 hover:border-white/30'}`} data-id="element-234">
                      <input type="radio" name="docType" className="hidden" checked={docType === 'PASAPORTE'} onChange={() => setDocType('PASAPORTE')} data-id="element-235" />
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${docType === 'PASAPORTE' ? 'border-gaming-cyan' : 'border-gray-500'}`} data-id="element-236">
                        {docType === 'PASAPORTE' && <div className="w-2 h-2 bg-gaming-cyan rounded-full" data-id="element-237" />}
                      </div>
                      <span className="font-medium" data-id="element-238">Pasaporte</span>
                    </label>
                  </div>
                  {/* Document Number */}
                  <div data-id="element-239">
                    <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-240">
                      {docType === 'CEDULA' ? 'Número de Cédula' : 'Número de Pasaporte'}
                    </label>
                    <input type="text" name="docNumber" value={formData.docNumber} onChange={handleChange} className={`w-full bg-gaming-dark border ${errors.docNumber ? 'border-red-500' : 'border-white/10 focus:border-gaming-cyan'} rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-gaming-cyan transition-all`} placeholder={docType === 'CEDULA' ? 'Ej: 1712345678' : 'Ej: A1234567'} data-id="element-241" />
                    {errors.docNumber && <p className="text-red-500 text-xs mt-1" data-id="element-242">
                        {errors.docNumber}
                      </p>}
                  </div>
                  {/* Names */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-243">
                    <div data-id="element-244">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-245">
                        Apellido/s
                      </label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className={`w-full bg-gaming-dark border ${errors.lastName ? 'border-red-500' : 'border-white/10 focus:border-gaming-cyan'} rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-gaming-cyan transition-all`} placeholder="Ej: Pérez" data-id="element-246" />
                      {errors.lastName && <p className="text-red-500 text-xs mt-1" data-id="element-247">
                          {errors.lastName}
                        </p>}
                    </div>
                    <div data-id="element-248">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-249">
                        Nombre/s
                      </label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className={`w-full bg-gaming-dark border ${errors.firstName ? 'border-red-500' : 'border-white/10 focus:border-gaming-cyan'} rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-gaming-cyan transition-all`} placeholder="Ej: Juan" data-id="element-250" />
                      {errors.firstName && <p className="text-red-500 text-xs mt-1" data-id="element-251">
                          {errors.firstName}
                        </p>}
                    </div>
                  </div>
                  {/* Phone with Country Selector */}
                  <div data-id="element-252">
                    <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-253">
                      Teléfono Celular
                    </label>
                    <div className="flex" data-id="element-254">
                      <div className="relative" data-id="element-255">
                        <button type="button" onClick={() => setShowCountryDropdown(!showCountryDropdown)} className="bg-gaming-dark border border-white/10 border-r-0 rounded-l-lg px-3 py-3 flex items-center gap-2 text-gray-400 hover:bg-white/5 transition-colors" data-id="element-256">
                          <img src={selectedCountry.flag} alt={selectedCountry.name} className="w-5 h-auto rounded-sm" data-id="element-257" />
                          <span className="text-sm" data-id="element-258">
                            {selectedCountry.dialCode}
                          </span>
                          <ChevronDown className="w-4 h-4" data-id="element-259" />
                        </button>

                        <AnimatePresence data-id="element-260">
                          {showCountryDropdown && <motion.div initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} className="absolute top-full left-0 mt-1 w-64 bg-gaming-card border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50" data-id="element-261">
                              {countries.map(country => <button key={country.code} type="button" onClick={() => {
                          setSelectedCountry(country);
                          setShowCountryDropdown(false);
                        }} className="w-full px-4 py-2 flex items-center gap-3 hover:bg-white/5 transition-colors text-left" data-id="element-262">
                                  <img src={country.flag} alt={country.name} className="w-5 h-auto rounded-sm" data-id="element-263" />
                                  <span className="text-white text-sm flex-1" data-id="element-264">
                                    {country.name}
                                  </span>
                                  <span className="text-gray-400 text-sm" data-id="element-265">
                                    {country.dialCode}
                                  </span>
                                </button>)}
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={`flex-1 bg-gaming-dark border ${errors.phone ? 'border-red-500' : 'border-white/10 focus:border-gaming-cyan'} rounded-r-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-gaming-cyan transition-all`} placeholder="99 123 4567" data-id="element-266" />
                    </div>
                    {errors.phone && <p className="text-red-500 text-xs mt-1" data-id="element-267">
                        {errors.phone}
                      </p>}
                  </div>
                  {/* Email */}
                  <div data-id="element-268">
                    <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-269">
                      Correo Electrónico
                    </label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className={`w-full bg-gaming-dark border ${errors.email ? 'border-red-500' : 'border-white/10 focus:border-gaming-cyan'} rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-gaming-cyan transition-all`} placeholder="juan.perez@ejemplo.com" data-id="element-270" />
                    {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-271">
                        {errors.email}
                      </p>}
                  </div>
                  {/* Terms Checkbox */}
                  <div className="pt-2" data-id="element-272">
                    <label className="flex items-start gap-3 cursor-pointer group" data-id="element-273">
                      <div className="relative mt-0.5" data-id="element-274">
                        <input type="checkbox" name="acceptedTerms" checked={formData.acceptedTerms} onChange={handleChange} className="peer sr-only" data-id="element-275" />
                        <div className={`w-5 h-5 border rounded transition-all flex items-center justify-center ${formData.acceptedTerms ? 'bg-gaming-cyan border-gaming-cyan' : 'border-gray-500 bg-transparent group-hover:border-gaming-cyan'}`} data-id="element-276">
                          {formData.acceptedTerms && <Check className="w-3.5 h-3.5 text-gaming-dark" data-id="element-277" />}
                        </div>
                      </div>
                      <span className="text-sm text-gray-400 leading-tight" data-id="element-278">
                        Acepto las{' '}
                        <button type="button" onClick={() => setShowPrivacyModal(true)} className="text-gaming-cyan hover:underline" data-id="element-279">
                          Políticas de Privacidad
                        </button>{' '}
                        y los{' '}
                        <button type="button" onClick={() => setShowTermsModal(true)} className="text-gaming-cyan hover:underline" data-id="element-280">
                          Términos y Condiciones
                        </button>
                      </span>
                    </label>
                    {errors.acceptedTerms && <p className="text-red-500 text-xs mt-1 ml-8" data-id="element-281">
                        {errors.acceptedTerms}
                      </p>}
                  </div>
                  {/* Submit Button */}
                  <button type="submit" disabled={isSubmitting} className="w-full mt-4 bg-gradient-to-r from-gaming-cyan to-gaming-cyan/80 text-gaming-dark font-bold text-lg py-4 rounded-lg shadow-[0_0_20px_rgba(0,217,255,0.4)] hover:shadow-[0_0_30px_rgba(0,217,255,0.6)] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-wide" data-id="element-282">
                    {isSubmitting ? 'Procesando...' : 'Suscribirme'}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} data-id="element-283" />
      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} data-id="element-284" />
    </>;
}